import { useEffect, useState } from "react";
import { api } from "../api.ts";
import { enlaceWhatsApp, celularInternacional } from "../../../dominio/src/index.ts";
import { AvisoError, useAccion } from "./comun.tsx";

interface Estado {
  estado: string; dias: number | null; soloLectura: boolean; vence: string; tieneLicencia: boolean;
  instalacion: string; clinica: string; ruc: string; usuarios: number | null; sedes: number | null; nombrePlan?: string;
}
interface Datos { licencia: { nombreEstado: string }; consultorio: Estado | null; ventas: { whatsapp: string; correo: string } | null }

const fecha = (iso: string) => new Date(iso + "T12:00:00").toLocaleDateString("es-EC", { day: "numeric", month: "long", year: "numeric" });

/**
 * Configuración → «Licencia» (versión para consultorios): el código de esta instalación, el estado de la licencia y dónde pegar la licencia nueva.
 * La licencia la emite el equipo de Donna para ese código y se comprueba sin internet.
 */
export function LicenciaConsultorio() {
  const [d, setD] = useState<Datos | null>(null);
  const [texto, setTexto] = useState("");
  const [msg, setMsg] = useState("");
  const [copiado, setCopiado] = useState(false);
  const a = useAccion();
  const cargar = () => api<Datos>("GET", "/api/licencia").then(setD).catch(a.setError);
  useEffect(() => { cargar(); }, []);

  async function activar() {
    const r = await a.correr(() => api("POST", "/api/licencia/consultorio", { licencia: texto.trim() }));
    if (r) { setTexto(""); setMsg("Licencia activada. Recarga la página para que todo se actualice y acepta el contrato de licencia que aparece en Inicio."); await cargar(); }
  }
  function leerArchivo(f: File | undefined) {
    if (!f) return;
    const lector = new FileReader();
    // al elegir el archivo de la licencia se activa solo (un toque menos)
    lector.onload = async () => {
      const t = String(lector.result ?? "").trim();
      setTexto(t);
      if (/^DONNA1\.[\w-]+\.[\w-]+$/.test(t.replace(/\s+/g, ""))) {
        const r = await a.correr(() => api("POST", "/api/licencia/consultorio", { licencia: t }));
        if (r) { setTexto(""); setMsg("Licencia activada. Recarga la página para que todo se actualice y acepta el contrato de licencia que aparece en Inicio."); await cargar(); }
      }
    };
    lector.readAsText(f);
  }
  async function buscar() {
    const r = await a.correr(() => api<{ resultado: string }>("POST", "/api/licencia/consultorio/buscar"));
    if (!r) return;
    setMsg(r.resultado === "activada" ? "Encontramos tu licencia y quedó activada. Recarga la página y acepta el contrato de licencia que aparece en Inicio."
      : r.resultado === "sin-licencia" ? "Donna ya sabe que instalaste el programa, pero todavía no te emite la licencia. Se activará sola cuando esté lista."
      : r.resultado === "sin-conexion" ? "No pudimos conectar con Donna. Revisa tu internet o pega la licencia a mano más abajo."
      : "Tu licencia ya está al día.");
    await cargar();
  }
  async function copiar(t: string) { try { await navigator.clipboard.writeText(t); setCopiado(true); setTimeout(() => setCopiado(false), 2000); } catch { /* el usuario lo copia a mano */ } }

  const e = d?.consultorio;
  const clase = !e ? "info" : e.soloLectura ? "error" : e.estado === "gracia" || e.estado === "por_vencer" ? "error" : e.tieneLicencia ? "ok" : "info";
  const frase = !e ? "Revisando…" : e.soloLectura
    ? `La licencia venció el ${fecha(e.vence)}: Donna está en solo lectura (puedes ver y respaldar tus datos, pero no registrar nada nuevo) hasta cargar una licencia nueva.`
    : e.estado === "gracia" ? `La licencia venció el ${fecha(e.vence)}. Donna sigue funcionando unos días más y luego pasará a solo lectura.`
    : e.tieneLicencia ? `Licencia vigente hasta el ${fecha(e.vence)}${e.dias !== null && e.dias <= 30 ? ` (quedan ${e.dias} día${e.dias === 1 ? "" : "s"})` : ""}.`
    : `Prueba gratis hasta el ${fecha(e.vence)}${e.dias !== null ? ` (quedan ${e.dias} día${e.dias === 1 ? "" : "s"})` : ""}. Tu licencia anual llega sola cuando Donna la emita (con internet).`;

  return (
    <div className="card" style={{ boxShadow: "none", display: "grid", gap: 12 }}>
      <h3 style={{ margin: 0 }}>Licencia de Donna</h3>
      <div className={`aviso ${clase}`} role="status">{frase}</div>
      {e && (
        <>
          <div>
            <small className="muted">Código de esta instalación (Donna lo recibe solo con internet; si prefieres, mándaselo tú)</small>
            <div className="fila" style={{ gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <b className="mono" style={{ fontSize: 20, letterSpacing: ".04em" }}>{e.instalacion}</b>
              <button className="btn chico" onClick={() => copiar(e.instalacion)}>{copiado ? "Copiado" : "Copiar"}</button>
              {d?.ventas?.whatsapp
                ? <a className="btn chico pri" target="_blank" rel="noopener" href={enlaceWhatsApp(celularInternacional(d.ventas.whatsapp) ?? d.ventas.whatsapp.replace(/\D/g, ""), `Hola, quiero la licencia de Donna para mi consultorio. Mi código de instalación es ${e.instalacion}.`)}>Pedir mi licencia por WhatsApp</a>
                : <button className="btn chico pri" onClick={() => copiar(`Hola, quiero la licencia de Donna para mi consultorio. Mi código de instalación es ${e.instalacion}.`)}>Copiar mensaje para pedir mi licencia</button>}
            </div>
          </div>
          {e.tieneLicencia && (
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              <li>Clínica: <b>{e.clinica || "—"}</b>{e.ruc ? ` · RUC ${e.ruc}` : ""}</li>
              {e.nombrePlan && <li>Plan: <b>{e.nombrePlan}</b></li>}
              <li>Vence: <b>{fecha(e.vence)}</b></li>
              {e.usuarios !== null && <li>Usuarios permitidos: <b>{e.usuarios}</b></li>}
              {e.sedes !== null && <li>Sedes permitidas: <b>{e.sedes}</b></li>}
            </ul>
          )}
        </>
      )}
      <div className="fila" style={{ gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <button className="btn pri" onClick={buscar} disabled={a.ocupado}>{a.ocupado ? "Buscando…" : "Buscar mi licencia ahora"}</button>
        <small className="muted">Con internet, Donna busca sola tu licencia y cada renovación; no tienes que hacer nada.</small>
      </div>
      <h4 style={{ margin: "8px 0 0" }}>Sin internet: cargar una licencia a mano</h4>
      <p style={{ margin: 0 }}>Pega aquí el texto de la licencia que te envió Donna (empieza con <span className="mono">DONNA1.</span>), o elige el archivo <span className="mono">.licencia</span>.</p>
      <textarea className="entrada mono" rows={4} value={texto} onChange={(x) => setTexto(x.target.value)} placeholder="DONNA1.…" spellCheck={false} style={{ width: "100%" }} />
      <div className="fila" style={{ flexWrap: "wrap", gap: 8, alignItems: "center" }}>
        <label className="btn chico">Elegir archivo<input type="file" accept=".licencia,.txt,text/plain" hidden onChange={(x) => leerArchivo(x.target.files?.[0])} /></label>
        <button className="btn pri" onClick={activar} disabled={a.ocupado || texto.trim().length < 20}>Activar licencia</button>
      </div>
      <AvisoError error={a.error} />
      {msg && <div className="aviso ok" role="status">{msg}</div>}
      {d?.ventas && (d.ventas.whatsapp || d.ventas.correo) && <small className="muted">¿Dudas o renovación? {d.ventas.whatsapp ? `WhatsApp +${d.ventas.whatsapp}` : ""}{d.ventas.whatsapp && d.ventas.correo ? " · " : ""}{d.ventas.correo}</small>}
      <small className="muted">La licencia se comprueba en este computador, sin internet. Si vence, Donna pasa a solo lectura: tus datos nunca se pierden.</small>
      <h4 style={{ margin: "12px 0 0" }}>Usar Donna desde el celular o la tableta</h4>
      {window.location.protocol === "https:" && window.location.hostname !== "localhost"
        ? <div className="aviso ok" role="status">Estás usando la dirección segura: <b className="mono">{window.location.origin}</b>. Abre esa misma dirección en tus celulares y tabletas (con la app Tailscale y la misma cuenta). El micrófono y la cámara funcionan.</div>
        : (
          <ol style={{ margin: 0, paddingLeft: 18 }}>
            <li>En el computador donde está instalada Donna, haz doble clic en <b>«Activar acceso desde celular.bat»</b> (carpeta de Donna). Te guía para instalar Tailscale, una app gratuita que crea una red privada solo para tus aparatos.</li>
            <li>Instala la app <b>Tailscale</b> en cada celular o tableta y entra con la misma cuenta.</li>
            <li>Abre en el navegador la dirección <span className="mono">https://…</span> que te muestra el programa. Con esa dirección el dictado por micrófono también funciona.</li>
          </ol>
        )}
    </div>
  );
}
